import { useCallback, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import type { GordianSeat } from "@b2bportal/air-seats-api";

import type { HopperSeatMapOptions } from "@checkout/components/SeatSelection/types";
import type { HopperSeatTotal } from "@checkout/states/Products/Flight/states/SeatSelection/context";
import { trackEvent } from "@hopper-b2b/api";
import {
  commonSelectors,
  DesktopLayout,
  FlightPassengerSelectors,
  FlightSelectors,
  getChildState,
  ReviewSelectors,
  type SeatEvents,
  SeatEventTypes,
  type SeatMapOptions,
  SeatSelectionErrorModal,
  SeatSelectionState,
  SeatSelectors,
  useCheckoutSend,
  useCheckoutStateSelector as useSelector,
} from "@hopper-b2b/checkout";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  type FlightClientAssetProps,
  type GordianSeatSegment,
  REQUESTED_SEATMAP,
} from "@hopper-b2b/types";
import {
  B2BLoadingPopup,
  B2BSpinner,
  ErrorModal,
  WorkingBunnies,
} from "@hopper-b2b/ui";
import { useDeviceTypes, useUberBridge } from "@hopper-b2b/utilities";

import Error from "../../../../assets/client/error.svg";
import { PATH_FLIGHTS_SEARCH } from "../../../../utils/urlPaths";
import { uberVariables } from "../../../defaultTheme";
import { ConnectedPriceBreakdownContent } from "../PriceBreakdownDropdown";
import { SelectedSeats } from "./components/";
import "./styles.scss";

export const ConnectedSeatSelection = () => {
  const { t } = useI18nContext();

  const send = useCheckoutSend<SeatEvents>();
  const history = useHistory();

  const clientAssets = useSelector(FlightSelectors.getClientAssets);
  const tripDetails = useSelector(FlightSelectors.getSelectedTripDetailsParent);
  const passengers = useSelector(
    FlightPassengerSelectors.getAllSelectedUserPassengersParent
  );

  const isLoading = useSelector(SeatSelectors.getIsSeatSelectionLoading);
  const selectedSeats = useSelector(SeatSelectors.getSelectedSeats);
  const selectedSeatSegments = useSelector(
    SeatSelectors.getSelectedSeatSegments
  );
  const seatSlices = useSelector(SeatSelectors.getSeatSlices);
  const cheapestSeat = useSelector(SeatSelectors.getCheapestSeat);
  const seatMapHtml = useSelector(SeatSelectors.getSeatMapHtml);
  const totalSeatPricing = useSelector(SeatSelectors.getSeatTotalPricing);
  const airports = useSelector(FlightSelectors.getAirportMap);
  const editSeatsFromReview = useSelector(
    ReviewSelectors.getEditSeatsFromReview
  );

  const goBack = useCallback(() => history.goBack(), [history]);
  const goNext = useCallback(() => {
    send(SeatEventTypes.NEXT);
  }, [send]);
  const { setHeader } = useUberBridge();

  useEffect(() => {
    setHeader({ title: t("seatSelection") });
  }, [setHeader, t]);

  const setSelectedSeats = useCallback(
    (
      seats: GordianSeat[],
      seatSegments: GordianSeatSegment[],
      selectedSeatTotal?: HopperSeatTotal
    ) => {
      send({
        type: SeatEventTypes.SELECT_SEATS,
        seats,
        seatSegments,
        selectedSeatTotal,
      });
      if (editSeatsFromReview) {
        goNext();
      }
    },
    [editSeatsFromReview, goNext, send]
  );

  const handleSelectSeats = useCallback(() => {
    if (selectedSeats.length > 0) {
      goNext();
    } else {
      trackEvent({
        eventName: REQUESTED_SEATMAP,
        properties: {},
      });
    }
  }, [goNext, selectedSeats.length]);

  return (
    <>
      {isLoading ? (
        <LoadingModal clientAssets={clientAssets} />
      ) : (
        <SelectedSeats
          tripDetails={tripDetails}
          seatSlices={seatSlices}
          airports={airports}
          cheapestSeat={cheapestSeat}
          selectedSeats={selectedSeats}
          passengers={passengers}
          totalSeatPricing={totalSeatPricing}
          seatMapHtml={seatMapHtml}
          selectedSeatSegments={selectedSeatSegments}
          fonts={['<link rel="stylesheet" href="../../../../fonts.scss">']}
          fontFamilies={['"Uber Move Text"']}
          onNext={goNext}
          onBack={goBack}
          onSelectSeats={handleSelectSeats}
          onSeatsSelected={setSelectedSeats}
        />
      )}
      <ConnectedErrorModal />
    </>
  );
};

const LoadingModal = ({
  clientAssets,
}: {
  clientAssets: Partial<FlightClientAssetProps> | undefined;
}) => {
  const { t } = useI18nContext();
  const { matchesMobile } = useDeviceTypes();
  const stateValue = useSelector(commonSelectors.getStateValue);
  const childState = getChildState(stateValue);
  const message = useMemo(() => {
    switch (childState) {
      case SeatSelectionState.validate:
        return t("seats.validateLoadingMessage");
      default:
        return t("seats.loadingMessage");
    }
  }, [childState, t]);

  return matchesMobile ? (
    <B2BLoadingPopup open fullScreen message={message} popupSize={"mobile"} />
  ) : (
    <>
      <DesktopLayout
        clientLogo={clientAssets?.logo}
        rightContent={<ConnectedPriceBreakdownContent />}
      >
        <B2BSpinner />
      </DesktopLayout>
      <B2BLoadingPopup
        open
        message={message}
        popupSize={"desktop"}
        loadingIcon={<WorkingBunnies />}
        className="seat-selection-loading"
      />
    </>
  );
};

export const ConnectedErrorModal = () => {
  const { t } = useI18nContext();
  const history = useHistory();
  const send = useCheckoutSend<SeatEvents>();
  const open = useSelector(SeatSelectors.getSeatSelectionErrorOpen);
  const contactError = useSelector(SeatSelectors.getSeatSelectionError);

  const clearError = () => send(SeatEventTypes.CLEAR_SEAT_ERROR);
  const goNext = () => send(SeatEventTypes.NEXT);

  const continueWithoutSeats = () => {
    clearError();
    goNext();
  };

  const searchAgain = () => {
    clearError();
    history.push(PATH_FLIGHTS_SEARCH);
  };

  const getErrorModalProps = (type?: SeatSelectionErrorModal) => {
    switch (type) {
      case SeatSelectionErrorModal.BrokenCart:
        return {
          title: t("checkoutError.genericErrorTitle"),
          subtitle: t("checkoutError.genericErrorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };

      case SeatSelectionErrorModal.FailedAddToCart:
        return {
          title: t("seats.errorFailedAddToCartTitle"),
          subtitle: t("seats.errorFailedAddToCartSubtitle"),
          primaryButtonText: t("seats.continueWithoutSeats"),
          primaryOnClick: continueWithoutSeats,
          secondaryButtonText: t("checkoutError.searchAgain"),
          secondaryOnClick: searchAgain,
        };

      case SeatSelectionErrorModal.GenericError:
        return {
          title: t("checkoutError.genericErrorTitle"),
          subtitle: t("checkoutError.genericErrorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      default:
        return {
          title: t("checkoutError.genericErrorTitle"),
          subtitle: t("checkoutError.genericErrorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
    }
  };
  const errorModalProps = getErrorModalProps(contactError?.type);

  return <ErrorModal open={open} {...errorModalProps} image={Error} />;
};
